import { useState } from 'react';
import {
  MEMBERSHIP_PRICE_MNT,
  THROWABLE_IDS,
  VOICE_TAUNT_IDS,
  type ThrowableId,
  type VoiceTauntId,
} from '@big2/shared';
import { VOICE_TAUNT_SCRIPTS } from '../../tableInteractionsSound.js';
import { Icon } from '../Icon.js';

export interface TargetPlayerInfo {
  playerId: string;
  name: string;
  seat: number;
  tier?: string;
}

interface TableInteractionPickerProps {
  target: TargetPlayerInfo;
  isVip: boolean;
  freeThrowsRemaining?: number;
  onClose: () => void;
  onThrow: (throwableId: ThrowableId) => void;
  onTaunt: (tauntId: VoiceTauntId) => void;
  onOpenVipModal: () => void;
  isMn?: boolean;
}

const THROWABLE_DETAILS: Record<ThrowableId, { icon: string; nameMn: string; nameEn: string; descMn: string }> = {
  tea: { icon: '🫖', nameMn: 'Халуун цай', nameEn: 'Hot Milk Tea', descMn: 'Дээгүүр нь цай цацах' },
  slipper: { icon: '🩴', nameMn: 'Улавч', nameEn: 'Slipper', descMn: 'Хөгжилтэй алгадах' },
  egg: { icon: '🥚', nameMn: 'Түүхий өндөг', nameEn: 'Raw Egg', descMn: 'Нүүр рүү нь нялах' },
  airag: { icon: '🍶', nameMn: 'Айраг', nameEn: 'Airag Bowl', descMn: 'Хултай айргаар мялаах' },
  cash: { icon: '💸', nameMn: 'Мөнгө', nameEn: 'Cash Rain', descMn: 'Мөнгөөр савлах' },
  shagai: { icon: '🦴', nameMn: 'Шагай', nameEn: 'Shagai', descMn: 'Шагай няслах' },
};

export function TableInteractionPicker({
  target,
  isVip,
  freeThrowsRemaining: _freeThrowsRemaining = 0,
  onClose,
  onThrow,
  onTaunt,
  onOpenVipModal,
  isMn = true,
}: TableInteractionPickerProps) {
  const [activeTab, setActiveTab] = useState<'throw' | 'voice'>('throw');
  const [showVipBanner, setShowVipBanner] = useState(false);

  const canInteract = isVip;

  const handleThrowClick = (id: ThrowableId) => {
    if (!canInteract) {
      setShowVipBanner(true);
      return;
    }
    onThrow(id);
    onClose();
  };

  const handleTauntClick = (id: VoiceTauntId) => {
    if (!canInteract) {
      setShowVipBanner(true);
      return;
    }
    onTaunt(id);
    onClose();
  };

  return (
    <div className="table-interaction-backdrop" onClick={onClose}>
      <div
        className="table-interaction-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={isMn ? 'Ширээний харилцан үйлдэл' : 'Table interaction'}
      >
        {/* Header */}
        <div className="table-interaction-header">
          <div className="target-summary">
            <span className="target-icon">🎯</span>
            <div className="target-info">
              <span className="target-title">{isMn ? 'Өрсөлдөгч рүү илгээх:' : 'Send to opponent:'}</span>
              <strong className="target-name">{target.name}</strong>
            </div>
          </div>
          <button className="close-button" onClick={onClose} aria-label={isMn ? 'Хаах' : 'Close'}>
            <Icon name="x" size={16} />
          </button>
        </div>

        {/* Quota / VIP Status Bar */}
        <div className={`status-pill ${isVip ? 'is-vip' : 'is-exhausted'}`}>
          {isVip ? (
            <span className="vip-badge-text">
              👑 {isMn ? 'VIP: Хязгааргүй шидэлт, тавлалт идэвхтэй' : 'VIP: Unlimited throws & voice taunts'}
            </span>
          ) : (
            <span className="quota-empty-text">
              🔒 {isMn ? 'Зөвхөн VIP гишүүд шидэлт, тавлалт ашиглах боломжтой' : 'VIP Only: Unlimited throws & voice taunts'}
            </span>
          )}
        </div>

        {/* Tab Buttons */}
        <div className="table-interaction-tabs">
          <button
            className={`tab-btn ${activeTab === 'throw' ? 'active' : ''}`}
            onClick={() => setActiveTab('throw')}
          >
            🎯 {isMn ? 'Шидэх' : 'Throw Items'}
          </button>
          <button
            className={`tab-btn ${activeTab === 'voice' ? 'active' : ''}`}
            onClick={() => setActiveTab('voice')}
          >
            🗣️ {isMn ? 'Монгол дуу' : 'Voice Taunts'}
          </button>
        </div>

        {/* Upsell Banner if exhausted */}
        {(!canInteract || showVipBanner) && (
          <div className="vip-upsell-box">
            <div className="vip-upsell-content">
              <strong>👑 {isMn ? 'VIP гишүүн болж хязгааргүй шидээрэй!' : 'Get VIP for unlimited throws!'}</strong>
              <p>
                {isMn
                  ? 'Өрсөлдөгчдөө шидэлт илгээж, хөгжилтэй монгол хоолойгоор тавлах боломжийг хязгааргүй эдлээрэй.'
                  : 'Send unlimited throwables and voice taunts at any time.'}
              </p>
              <button
                className="vip-unlock-btn"
                onClick={() => {
                  onClose();
                  onOpenVipModal();
                }}
              >
                👑 {isMn ? `VIP авах · ${MEMBERSHIP_PRICE_MNT.toLocaleString('mn-MN')}₮` : `Unlock VIP · ${MEMBERSHIP_PRICE_MNT.toLocaleString('en-US')} MNT`}
              </button>
            </div>
          </div>
        )}

        {/* Tab 1: Throw Items Grid */}
        {activeTab === 'throw' && (
          <div className="throw-grid">
            {THROWABLE_IDS.map((id) => {
              const item = THROWABLE_DETAILS[id];
              return (
                <button
                  key={id}
                  className="throw-item-btn"
                  onClick={() => handleThrowClick(id)}
                  title={isMn ? `${item.nameMn}: ${item.descMn}` : item.nameEn}
                >
                  <span className="throw-emoji">{item.icon}</span>
                  <span className="throw-name">{isMn ? item.nameMn : item.nameEn}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Tab 2: Voice Taunts List */}
        {activeTab === 'voice' && (
          <div className="voice-list">
            {VOICE_TAUNT_IDS.map((id) => {
              const taunt = VOICE_TAUNT_SCRIPTS[id];
              return (
                <button
                  key={id}
                  className="voice-item-btn"
                  onClick={() => handleTauntClick(id)}
                  title={isMn ? taunt.mn : taunt.en}
                >
                  <span className="voice-icon">{taunt.icon}</span>
                  <div className="voice-text-wrap">
                    <span className="voice-text-primary">"{isMn ? taunt.mn : taunt.en}"</span>
                  </div>
                  <span className="voice-play-cue">▶</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
